import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Puzzle.css';
import SolutionRenderer from './components/SolutionRenderer';
import { puzzleAPI } from './services/api';

const PuzzleSolver = () => {
  const navigate = useNavigate();
  const [gridSize, setGridSize] = useState(8);
  const [grid, setGrid] = useState(new Array(8).fill(null).map(() => new Array(8).fill({ color: 'black', counter: 0 })));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [solutionData, setSolutionData] = useState(null);
  const [renderedSolutions, setRenderedSolutions] = useState([]);
  const [error, setError] = useState('');
  const [highlightedPiece, setHighlightedPiece] = useState(null);

  const COLORS = ['red', 'blue', 'yellow', 'black'];
  const colorMapping = {
    red: 'r',
    blue: 'b',
    yellow: 'y',
    black: 'x',
  };

  const COLOR_CONSTRAINTS_4x4 = {
    red: { min: 4, max: 9 },
    blue: { min: 0, max: 1 },
    yellow: { min: 0, max: 1 },
    black: { min: 3, max: 12 }
  };

  const weightedRandomColor = (colorCounts, totalCells) => {
    const weights = {
      black: 11,
      red: 8,
      blue: 3,
      yellow: 3
    };

    for (const color in weights) {
      if (colorCounts[color] >= COLOR_CONSTRAINTS_4x4[color].max * (totalCells / 16)) {
        weights[color] = 0;
      } else if (colorCounts[color] < COLOR_CONSTRAINTS_4x4[color].min * (totalCells / 16)) {
        weights[color] *= 2;
      }
    }

    const totalWeight = Object.values(weights).reduce((sum, weight) => sum + weight, 0);
    const rand = Math.random() * totalWeight;

    let cumulativeWeight = 0;
    for (const [color, weight] of Object.entries(weights)) {
      cumulativeWeight += weight;
      if (rand <= cumulativeWeight) {
        return color;
      }
    }
  };

  const randomizeGrid = () => {
    const colorCounts = { red: 0, blue: 0, yellow: 0, black: 0 };
    const totalCells = gridSize * gridSize;

    const newGrid = new Array(gridSize).fill(null).map(() =>
      new Array(gridSize).fill(null).map(() => {
        let color;
        do {
          color = weightedRandomColor(colorCounts, totalCells);
        } while (
          colorCounts[color] >= COLOR_CONSTRAINTS_4x4[color].max * (totalCells / 16) ||
          (color !== 'black' && totalCells - Object.values(colorCounts).reduce((a, b) => a + b, 0) <= Math.ceil(totalCells / 4))
        );
        colorCounts[color]++;
        return { color, counter: 0 };
      })
    );

    for (const color in COLOR_CONSTRAINTS_4x4) {
      const minCount = Math.ceil(COLOR_CONSTRAINTS_4x4[color].min * (totalCells / 16));
      while (colorCounts[color] < minCount) {
        const randomRow = Math.floor(Math.random() * gridSize);
        const randomCol = Math.floor(Math.random() * gridSize);
        if (newGrid[randomRow][randomCol].color !== color) {
          colorCounts[newGrid[randomRow][randomCol].color]--;
          newGrid[randomRow][randomCol].color = color;
          colorCounts[color]++;
        }
      }
    }

    setGrid(newGrid);
    setSolutionData(null);
    setError('');
  };

  const handleGridSizeChange = (size) => {
    size = parseInt(size, 10);
    setGridSize(size);
    setGrid(new Array(size).fill(null).map(() => new Array(size).fill({ color: 'black', counter: 0 })));
    setSolutionData(null);
    setError('');
  };

  const updateColorAndCounter = (row, col) => {
    const newGrid = grid.map((rowArr, rowIndex) =>
      rowIndex === row
        ? rowArr.map((cell, colIndex) =>
          colIndex === col
            ? {
              ...cell,
              color: COLORS[(COLORS.indexOf(cell.color) + 1) % COLORS.length],
              counter: cell.counter + 1
            }
            : cell
        )
        : rowArr
    );
    setGrid(newGrid);
  };

  const clearGrid = () => {
    setGrid(new Array(gridSize).fill(null).map(() => new Array(gridSize).fill({ color: 'black', counter: 0 })));
    setSolutionData(null);
    setHighlightedPiece(null);
    setRenderedSolutions([]);
    setError('');
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');
    setSolutionData(null);
    setHighlightedPiece(null);
    setRenderedSolutions([]);

    const pattern = grid.map(row => row.map(cell => colorMapping[cell.color]));
    try {
      const data = await puzzleAPI.solvePuzzle(pattern);
      setSolutionData(data);
      if (data.rendered_solutions) {
        setRenderedSolutions(data.rendered_solutions);
      } else {
        setRenderedSolutions([]);
      }
    } catch (error) {
      console.error('Error:', error);
      setError(error.response?.data?.message || 'Failed to solve puzzle');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddPuzzle = () => {
    const puzzleData = {
      grid: grid,
      gridSize: gridSize
    };
    navigate('/fill/puzzle', { state: { puzzleData } });
  };

  return (
    <div className="challenge-container">
      <h2>Puzzle Solver</h2>
      <select
        value={gridSize}
        onChange={(e) => handleGridSizeChange(e.target.value)}
        className="select-field"
      >
        <option value="4">4x4</option>
        <option value="5">5x5</option>
        <option value="6">6x6</option>
        <option value="8">8x8</option>
      </select>
      <div className="grid-container">
        <div className={`grid grid-${gridSize}x${gridSize}`} id="gc">
          {grid.map((row, rowIndex) => (
            <React.Fragment key={rowIndex}>
              {row.map((cell, colIndex) => (
                <div
                  key={colIndex}
                  className="grid-cell"
                  style={{ backgroundColor: cell.color }}
                  onClick={() => updateColorAndCounter(rowIndex, colIndex)}
                ></div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="button-panel">
        <button className="randomize-button" onClick={randomizeGrid}>
          Randomize Grid
        </button>
        <button className="try-solve-button" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? <div className="loader"></div> : 'Solve Puzzle'}
        </button>
        <button className="add-puzzle-button" onClick={handleAddPuzzle} disabled={isSubmitting}>
          Add Puzzle
        </button>
        <button className="clear-button" onClick={clearGrid}>
          Clear Grid
        </button>
      </div>
      {error && <p className="error-message">{error}</p>}
      {solutionData && (
        <div className="solution-info">
          <h3>Solution Information</h3>
          <p>Number of solutions: {solutionData.num_solutions}</p>
          <div className="solutions-container">
            {renderedSolutions.map((solution, index) => (
              <SolutionRenderer
                key={index}
                solution={solution}
                gridSize={gridSize}
                highlightedPiece={highlightedPiece}
                setHighlightedPiece={setHighlightedPiece}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PuzzleSolver;
